import {
  motion,
  AnimatePresence,
  useReducedMotion,
  type Transition,
  type Variants,
  type HTMLMotionProps,
} from 'framer-motion';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { haptic } from './haptics';

// ─────────────────────────────────────────────────────────────────────────────
// Squishy motion kit. One shared vocabulary of springs, entrances, presses,
// celebrations, and count-ups so the whole app moves as one system. Built on
// framer-motion; respects prefers-reduced-motion everywhere. Import these
// instead of hand-rolling transitions so screens stay consistent.
// ─────────────────────────────────────────────────────────────────────────────

/** Spring presets — the app's physical feel. */
export const springs = {
  /** Gentle, for large surfaces / page content. */
  soft: { type: 'spring', stiffness: 260, damping: 30, mass: 0.9 } as Transition,
  /** Quick and tight, for button presses / small UI. */
  snappy: { type: 'spring', stiffness: 500, damping: 32 } as Transition,
  /** Playful overshoot, for celebrations / pop-ins. */
  bouncy: { type: 'spring', stiffness: 420, damping: 16, mass: 0.8 } as Transition,
} as const;

export { motion, AnimatePresence, useReducedMotion };

// ── Press feedback ───────────────────────────────────────────────────────────

/** Props to spread onto any motion element for a satisfying tap-press. */
export const tapProps = {
  whileTap: { scale: 0.96 },
  transition: springs.snappy,
} as const;

type PressableButtonProps = HTMLMotionProps<'button'> & {
  /** Fire a haptic on press. Default: 'light'. Pass null to disable. */
  haptic?: keyof typeof haptic | null;
};

/**
 * A button that scales on press and fires a haptic. Drop-in replacement for
 * <button>; forwards className, onClick, disabled, children, etc.
 */
export function Pressable({ haptic: h = 'light', onPointerDown, ...props }: PressableButtonProps) {
  const reduce = useReducedMotion();
  return (
    <motion.button
      {...props}
      whileTap={reduce ? undefined : { scale: 0.96 }}
      transition={springs.snappy}
      onPointerDown={(e) => {
        if (!props.disabled && h) haptic[h]();
        onPointerDown?.(e);
      }}
    />
  );
}

// ── Entrances ────────────────────────────────────────────────────────────────

/** Fade + rise in. Use for a single element appearing. */
export function FadeIn({
  children,
  delay = 0,
  y = 12,
  className,
}: { children: ReactNode; delay?: number; y?: number; className?: string }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? { opacity: 0 } : { opacity: 0, y }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...springs.soft, delay }}
    >
      {children}
    </motion.div>
  );
}

/** Container that staggers its <Stagger.Item> children in on mount. */
const staggerContainer: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06, delayChildren: 0.04 } },
};
const staggerItem: Variants = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: springs.soft },
};

export function Stagger({ children, className }: { children: ReactNode; className?: string }) {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div className={className} variants={staggerContainer} initial="hidden" animate="show">
      {children}
    </motion.div>
  );
}
Stagger.Item = function StaggerItemEl({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <motion.div className={className} variants={staggerItem}>
      {children}
    </motion.div>
  );
};

/** Page-level wrapper: soft fade+slide as a route mounts. */
export function PageTransition({ children, className }: { children: ReactNode; className?: string }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

// ── Modal / bottom sheet ─────────────────────────────────────────────────────

/**
 * Animated modal. Renders a backdrop + centered card (sheet="center") or a
 * bottom sheet (sheet="bottom"). Handles enter/exit via AnimatePresence — keep
 * it mounted and toggle `open`.
 */
export function Modal({
  open,
  onClose,
  children,
  sheet = 'center',
  className = '',
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  sheet?: 'center' | 'bottom';
  className?: string;
}) {
  const bottom = sheet === 'bottom';
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex justify-center px-4"
          style={{ alignItems: bottom ? 'flex-end' : 'center' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            className={`relative w-full ${bottom ? 'max-w-lg' : 'max-w-sm'} ${className}`}
            initial={bottom ? { y: '100%' } : { opacity: 0, scale: 0.92, y: 12 }}
            animate={bottom ? { y: 0 } : { opacity: 1, scale: 1, y: 0 }}
            exit={bottom ? { y: '100%' } : { opacity: 0, scale: 0.95, y: 8 }}
            transition={bottom ? springs.soft : springs.bouncy}
            style={{ paddingBottom: bottom ? 'env(safe-area-inset-bottom)' : undefined }}
          >
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ── Count-up number ──────────────────────────────────────────────────────────

/** Animate a number from its previous value to `value` on change. */
export function useCountUp(value: number, duration = 700) {
  const [display, setDisplay] = useState(value);
  const prev = useRef(value);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (reduce || value === prev.current) { setDisplay(value); prev.current = value; return; }
    const from = prev.current;
    const to = value;
    prev.current = value;
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(from + (to - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration, reduce]);

  return display;
}

export function CountUp({ value, className, format }: {
  value: number;
  className?: string;
  format?: (n: number) => string;
}) {
  const n = useCountUp(value);
  return <span className={className}>{format ? format(n) : n.toLocaleString()}</span>;
}

// ── Confetti burst ───────────────────────────────────────────────────────────

const CONFETTI_COLORS = ['#2dd4bf', '#fbbf24', '#6b46c1', '#f472b6', '#38bdf8', '#34d399'];

/**
 * One-shot confetti burst. Mount it (e.g. on a results screen) and it plays
 * once. Purely decorative and pointer-events-none, so it never blocks UI.
 */
export function Confetti({ count = 32, duration = 1.5 }: { count?: number; duration?: number }) {
  const reduce = useReducedMotion();
  const pieces = useRef(
    Array.from({ length: count }, (_, i) => ({
      id: i,
      x: (i / count) * 100,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length]!,
      delay: (i % 8) * 0.05,
      drift: (i % 2 ? 1 : -1) * (20 + (i % 5) * 12),
      rotate: (i % 2 ? 1 : -1) * (180 + (i % 4) * 90),
      size: 6 + (i % 3) * 3,
    })),
  );
  if (reduce) return null;

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      {pieces.current.map((p) => (
        <motion.span
          key={p.id}
          className="absolute top-0 block rounded-[2px]"
          style={{ left: `${p.x}%`, width: p.size, height: p.size * 1.4, backgroundColor: p.color }}
          initial={{ y: '-10%', opacity: 1, rotate: 0 }}
          animate={{ y: '110vh', x: p.drift, opacity: [1, 1, 0], rotate: p.rotate }}
          transition={{ duration, delay: p.delay, ease: 'easeIn' }}
        />
      ))}
    </div>
  );
}

// ── Skeleton shimmer ─────────────────────────────────────────────────────────

/** Loading placeholder with a shimmer sweep (see .shimmer in styles.css). */
export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`shimmer rounded-xl bg-slate-200/70 ${className}`} />;
}
